/**
 * Auth hook + helpers for Emergent-managed Google Social login.
 *
 * Flow:
 *   1. User clicks Sign in → we redirect to
 *      https://auth.emergentagent.com/?redirect={window.location.origin}/today
 *   2. On return, Emergent appends `#session_id=XYZ` to the URL.
 *   3. Our AuthBootstrap component detects that hash, POSTs it to
 *      /api/auth/session (which sets an httpOnly cookie), then cleans the URL.
 *   4. useAuth() polls /api/auth/me for the current identity.
 */
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Important: the backend sets an httpOnly cookie; axios must send credentials.
axios.defaults.withCredentials = true;

let _cached = null;
const listeners = new Set();

function emit(next) {
    _cached = next;
    listeners.forEach((fn) => {
        try {
            fn(next);
        } catch (err) {
            // A subscriber threw — don't let it stop the rest of the
            // listeners from receiving the update.
            console.warn("[auth] listener threw:", err);
        }
    });
}

export async function loadMe() {
    try {
        const { data } = await axios.get(`${API}/auth/me`);
        emit(data);
        return data;
    } catch (e) {
        // Network error / 5xx / CORS. Treat the user as unauthenticated
        // so the UI rerenders the SignInGate, but log so the cause is
        // discoverable in the browser console during incidents.
        if (e?.response?.status !== 401) {
            console.warn("[auth] loadMe failed:", e?.response?.status || e.message);
        }
        const data = { authenticated: false, auth_enabled: true };
        emit(data);
        return data;
    }
}

export async function postSessionId(session_id) {
    const { data } = await axios.post(`${API}/auth/session`, { session_id });
    await loadMe();
    return data;
}

export async function logout() {
    try {
        await axios.post(`${API}/auth/logout`);
    } catch (err) {
        // Best-effort logout. Even if the server call fails, we still
        // emit the unauthenticated state below so the UI clears.
        console.warn("[auth] server logout failed:", err?.response?.status || err.message);
    }
    emit({ authenticated: false, auth_enabled: true });
}

export function redirectToSignIn() {
    const redirect = `${window.location.origin}/today`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
        redirect,
    )}`;
}

export function useAuth() {
    const [me, setMe] = useState(_cached);
    const [loading, setLoading] = useState(_cached == null);

    useEffect(() => {
        const l = (next) => setMe(next);
        listeners.add(l);
        if (_cached == null) {
            loadMe().finally(() => setLoading(false));
        }
        return () => listeners.delete(l);
    }, []);

    const role = me?.role || null;
    const email = me?.email || null;

    return useMemo(
        () => ({
            loading,
            me,
            authenticated: !!me?.authenticated,
            email,
            role,
            isAdmin: role === "admin",
            isManagerOrAdmin: role === "admin" || role === "manager",
            logout,
            refresh: loadMe,
        }),
        [loading, me, role, email],
    );
}

/** Sets a global axios interceptor that redirects to sign-in on 401. */
export function install401Redirect() {
    axios.interceptors.response.use(
        (r) => r,
        (err) => {
            const status = err?.response?.status;
            if (status === 401) {
                emit({ authenticated: false, auth_enabled: true });
            }
            return Promise.reject(err);
        },
    );
}

export function useAuthBootstrap() {
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        install401Redirect();
        const url = new URL(window.location.href);
        const hash = window.location.hash || "";
        let sessionId = null;
        if (hash.includes("session_id=")) {
            const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
            sessionId = hashParams.get("session_id");
        }

        (async () => {
            if (sessionId) {
                try {
                    await postSessionId(sessionId);
                    // Clear the hash
                    history.replaceState({}, "", url.pathname + url.search);
                } catch (e) {
                    const msg = e?.response?.data?.detail || e.message || "Sign-in failed";
                    setError(String(msg));
                }
            }
            await loadMe();
            setReady(true);
        })();
    }, []);

    return { ready, error };
}

export const AUTH_RESTRICTED_MESSAGE =
    "This Google account is not authorised to use ProvidersCSV. " +
    "Ask an admin to add your email to the allow-list.";
